/**
 * Sponsorship Application Form Script
 * Handles automatic Google OAuth Access Token refresh, direct Google Drive API file upload
 * (with supportsAllDrives=true for client-shared folders), shareable link creation, Asana task formatting, and submission.
 */

document.addEventListener('DOMContentLoaded', function() {
  initSponsorshipForms();
});

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function() {
    initSponsorshipForms();
  });
}

function initSponsorshipForms() {
  const sections = document.querySelectorAll('.sponsorship-app-section');
  
  sections.forEach(function(section) {
    const form = section.querySelector('.sponsorship-app__form');
    if (!form || form.dataset.initialized === 'true') return;
    form.dataset.initialized = 'true';

    const submitBtn = section.querySelector('.sponsorship-app__submit-btn');
    const fileInput = section.querySelector('.sponsorship-app__file-input');
    const fileListDisplay = section.querySelector('.sponsorship-app__file-list');
    const dateInput = section.querySelector('.sponsorship-app__hidden-date');
    const dateDisplay = section.querySelector('.sponsorship-app__date-display');

    const alertSuccess = section.querySelector('.sponsorship-app__alert--success');
    const alertError = section.querySelector('.sponsorship-app__alert--error');
    const alertWarning = section.querySelector('.sponsorship-app__alert--warning');

    const gdriveClientId = (form.dataset.gdriveClientId || '').replace(/\s+/g, '');
    const gdriveClientSecret = (form.dataset.gdriveClientSecret || '').replace(/\s+/g, '');
    const gdriveRefreshToken = (form.dataset.gdriveRefreshToken || '').replace(/\s+/g, '');
    let fallbackAccessToken = (form.dataset.gdriveAccessToken || '').replace(/\s+/g, '');

    const gdriveFolderId = (form.dataset.gdriveFolderId || '1JIHCKaFBwgAIgBBh_GPqTDenEuPoXOGH').replace(/\s+/g, '');
    const asanaPat = (form.dataset.asanaPat || '').trim();
    const asanaProjectGid = (form.dataset.asanaProjectGid || '').trim();
    const endpointUrl = (form.dataset.endpointUrl || '').trim();

    // Set today's date automatically in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];
    if (dateInput) dateInput.value = today;
    if (dateDisplay) dateDisplay.textContent = today;

    // File selection UI feedback
    if (fileInput && fileListDisplay) {
      fileInput.addEventListener('change', function() {
        if (this.files && this.files.length > 0) {
          const names = Array.from(this.files).map(f => f.name).join(', ');
          fileListDisplay.textContent = `Selected (${this.files.length}): ${names}`;
        } else {
          fileListDisplay.textContent = '';
        }
      });
    }

    function hideAlerts() {
      if (alertSuccess) alertSuccess.style.display = 'none';
      if (alertError) alertError.style.display = 'none';
      if (alertWarning) alertWarning.style.display = 'none';
    }

    function slugify(text) {
      return (text || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9 -]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
    }

    /**
     * Programmatic OAuth Token Refresh
     */
    async function getFreshGoogleAccessToken() {
      // 1. Refresh Token Flow (token starts with '1//')
      if (gdriveClientId && gdriveClientSecret && gdriveRefreshToken && gdriveRefreshToken.startsWith('1//')) {
        console.log('Refreshing Google OAuth Access Token via https://oauth2.googleapis.com/token ...');
        try {
          const tokenRes = await fetch('https://oauth2.googleapis.com/token', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: new URLSearchParams({
              client_id: gdriveClientId,
              client_secret: gdriveClientSecret,
              refresh_token: gdriveRefreshToken,
              grant_type: 'refresh_token'
            })
          });

          const text = await tokenRes.text();
          let resJson = {};
          try { resJson = JSON.parse(text); } catch(e){}

          if (tokenRes.ok && resJson.access_token) {
            console.log('Successfully acquired fresh Google Drive access token!');
            return resJson.access_token;
          } else {
            const errMsg = resJson.error_description || resJson.error || text || 'OAuth token refresh failed';
            console.error('Google OAuth Token Refresh failed:', resJson);
            throw new Error(`Google OAuth Token Refresh Error: ${errMsg}`);
          }
        } catch (e) {
          console.error('Error during Google OAuth token refresh:', e);
          throw e;
        }
      }

      // 2. Direct Access Token Detection fallback
      if (gdriveRefreshToken && gdriveRefreshToken.startsWith('ya29.')) {
        return gdriveRefreshToken;
      }
      if (fallbackAccessToken && fallbackAccessToken.startsWith('ya29.')) {
        return fallbackAccessToken;
      }

      throw new Error('Valid Google OAuth Access Token (ya29.) or Refresh Token (1//) is required.');
    }

    /**
     * Upload single file directly to Google Drive API & return shareable URL
     */
    async function uploadFileToGoogleDrive(file, namePrefix) {
      const activeAccessToken = await getFreshGoogleAccessToken();

      if (!activeAccessToken) {
        throw new Error('Google Drive Access Token is missing. Please enter Client ID, Client Secret, and Refresh Token in section settings.');
      }

      const ext = file.name.includes('.') ? '.' + file.name.split('.').pop() : '';
      const baseClean = file.name.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_');
      const uniqueFileName = `${namePrefix}-${baseClean}${ext}`;
      const folderCandidate = (gdriveFolderId || '1JIHCKaFBwgAIgBBh_GPqTDenEuPoXOGH').trim();

      async function executeUpload(targetFolderId, tokenToUse) {
        const metadata = {
          name: uniqueFileName,
          mimeType: file.type || 'application/octet-stream'
        };
        if (targetFolderId) {
          metadata.parents = [targetFolderId];
        }

        const boundary = '-------314159265358979323846';
        const delimiter = "\r\n--" + boundary + "\r\n";
        const close_delim = "\r\n--" + boundary + "--";

        const fileReader = new FileReader();
        const fileData = await new Promise((resolve, reject) => {
          fileReader.onload = (e) => resolve(e.target.result);
          fileReader.onerror = (e) => reject(e);
          fileReader.readAsArrayBuffer(file);
        });

        const contentType = file.type || 'application/octet-stream';
        const metadataPart = delimiter +
          'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
          JSON.stringify(metadata);

        const mediaPartHeader = delimiter +
          `Content-Type: ${contentType}\r\n\r\n`;

        const enc = new TextEncoder();
        const metadataBuffer = enc.encode(metadataPart + mediaPartHeader);
        const closeBuffer = enc.encode(close_delim);

        const fullPayload = new Uint8Array(
          metadataBuffer.byteLength + fileData.byteLength + closeBuffer.byteLength
        );

        fullPayload.set(new Uint8Array(metadataBuffer), 0);
        fullPayload.set(new Uint8Array(fileData), metadataBuffer.byteLength);
        fullPayload.set(new Uint8Array(closeBuffer), metadataBuffer.byteLength + fileData.byteLength);

        console.log('Uploading file to Google Drive (folder:', targetFolderId || 'My Drive Root', '):', uniqueFileName);

        const uploadResponse = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&supportsAllDrives=true', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokenToUse}`,
            'Content-Type': `multipart/related; boundary=${boundary}`
          },
          body: fullPayload
        });

        const text = await uploadResponse.text();
        let resData = {};
        try { resData = JSON.parse(text); } catch(e){}

        if (!uploadResponse.ok) {
          let msg = (resData.error && resData.error.message) ? resData.error.message : (text || `HTTP ${uploadResponse.status}`);
          const err = new Error(msg);
          err.status = uploadResponse.status;
          throw err;
        }

        return resData;
      }

      let uploadResult = null;

      // 1. Attempt upload to configured target folder
      try {
        uploadResult = await executeUpload(folderCandidate, activeAccessToken);
      } catch (firstErr) {
        if (firstErr.status === 401 || (firstErr.message && firstErr.message.includes('Expired'))) {
          throw firstErr;
        }
        console.warn(`Upload to folder ${folderCandidate} failed (${firstErr.message}). Retrying upload to My Drive root...`);
        // Fallback upload directly to user's root My Drive folder
        uploadResult = await executeUpload(null, activeAccessToken);
      }

      const fileId = uploadResult.id;
      console.log('File successfully uploaded to Google Drive! File ID:', fileId);

      // 2. Make file shareable (Anyone with link can read)
      try {
        await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}/permissions?supportsAllDrives=true`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${activeAccessToken}`,
            'Content-Type': `application/json`
          },
          body: JSON.stringify({
            role: 'reader',
            type: 'anyone'
          })
        });
      } catch (permErr) {
        console.warn('Could not set public permission on Google Drive file:', permErr);
      }

      // 3. Fetch webViewLink
      const fileDetailsRes = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?fields=webViewLink,webContentLink&supportsAllDrives=true`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${activeAccessToken}`
        }
      });

      if (fileDetailsRes.ok) {
        const detailsData = await fileDetailsRes.json();
        if (detailsData.webViewLink) {
          return detailsData.webViewLink;
        }
      }

      return `https://drive.google.com/file/d/${fileId}/view?usp=sharing`;
    }

    /**
     * Submit task directly to Asana API
     */
    async function postToAsana(taskTitle, taskDescription) {
      if (!asanaPat) {
        console.warn('Asana PAT missing. Skipping direct Asana creation.');
        return null;
      }

      const payload = {
        data: {
          name: taskTitle,
          notes: taskDescription,
          ...(asanaProjectGid ? { projects: [asanaProjectGid] } : {})
        }
      };

      const res = await fetch('https://app.asana.com/api/1.0/tasks', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${asanaPat}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const text = await res.text();
      let errJson = {};
      try { errJson = JSON.parse(text); } catch (e) {}

      if (!res.ok) {
        console.error('Asana API error:', errJson);
        throw new Error(errJson.errors ? errJson.errors.map(e => e.message).join(', ') : `Asana error ${res.status}`);
      }

      return errJson;
    }

    // Form Submit Handler
    form.addEventListener('submit', async function(e) {
      e.preventDefault();
      hideAlerts();

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const formData = new FormData(form);

      const fullName = (formData.get('full_name') || '').trim();
      const email = (formData.get('email') || '').trim();
      const phone = (formData.get('phone') || '').trim();

      const instagram = (formData.get('instagram') || '').trim();
      const tiktok = (formData.get('tiktok') || '').trim();
      const youtube = (formData.get('youtube') || '').trim();
      const facebook = (formData.get('facebook') || '').trim();
      const website = (formData.get('website') || '').trim();

      const truckYear = (formData.get('truck_year') || '').trim();
      const truckMake = (formData.get('truck_make') || '').trim();
      const truckModel = (formData.get('truck_model') || '').trim();
      const bedLength = (formData.get('bed_length') || '').trim();

      const coverInterested = (formData.get('cover_interested') || '').trim();
      const reasonPartner = (formData.get('reason_partner') || '').trim();
      const additionalResponses = (formData.get('additional_responses') || '').trim();
      const submissionDate = (formData.get('submission_date') || today).trim();

      const truckString = [truckYear, truckMake, truckModel].filter(Boolean).join(' ');
      let taskTitle = fullName;
      if (truckString) {
        taskTitle += ` | ${truckString}`;
      }

      const nameSlug = slugify(fullName) || 'applicant';
      const truckSlug = slugify(truckString) || 'truck';
      const filePrefix = `${nameSlug}-${truckSlug}-${submissionDate}`;

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
      }

      try {
        let gdriveLinks = [];

        // 1. Upload files to Google Drive if present
        if (fileInput && fileInput.files && fileInput.files.length > 0) {
          const hasCredentials = (gdriveClientId && gdriveClientSecret && gdriveRefreshToken) || fallbackAccessToken || gdriveRefreshToken;
          if (hasCredentials) {
            for (let i = 0; i < fileInput.files.length; i++) {
              const file = fileInput.files[i];
              try {
                const link = await uploadFileToGoogleDrive(file, filePrefix);
                gdriveLinks.push(link);
              } catch (fileErr) {
                console.error(`Error uploading file ${file.name} to Google Drive:`, fileErr);
                throw fileErr;
              }
            }
          } else {
            console.warn('Google Drive Credentials missing in section settings.');
          }
        }

        let attachmentText = '';
        if (gdriveLinks.length > 0) {
          attachmentText = `Attachment:\n${gdriveLinks.join('\n')}`;
        }

        // 2. Format Task Description
        const taskDescription = 
`Contact Information
-------------------
Full Name: ${fullName}
Email: ${email}
Phone: ${phone}

Social Media
------------
Instagram: ${instagram}
TikTok: ${tiktok}
YouTube: ${youtube}
Facebook: ${facebook}
Website: ${website}

Truck Information
-----------------
Truck Year: ${truckYear}
Truck Make: ${truckMake}
Truck Model: ${truckModel}
Bed Length: ${bedLength}

Sponsorship Details
-------------------
Cover Interested In: ${coverInterested}
Reason They Want to Partner with Peragon: ${reasonPartner}
Additional Responses: ${additionalResponses}

Additional Information
----------------------
Submission Date: ${submissionDate}${attachmentText ? '\n' + attachmentText : ''}`;

        formData.set('task_title', taskTitle);
        formData.set('task_description', taskDescription);

        if (gdriveLinks.length > 0) {
          const gdriveUrlString = gdriveLinks.join('\n');
          formData.set('photo_content', gdriveUrlString);
          formData.set('photo_url', gdriveUrlString);
          formData.set('gdrive_url', gdriveUrlString);
          formData.set('dropbox_url', gdriveUrlString);
          formData.set('gdrive_links', JSON.stringify(gdriveLinks));
          formData.set('dropbox_links', JSON.stringify(gdriveLinks));
        }

        // Log all payload fields to console
        const payloadObject = {};
        for (let [key, value] of formData.entries()) {
          if (value instanceof File) {
            payloadObject[key] = `[File: ${value.name} (${value.size} bytes)]`;
          } else {
            payloadObject[key] = value;
          }
        }
        console.group('=== Form Submission Payload (Google Drive & Asana) ===');
        console.log('Target Endpoint URL:', endpointUrl || 'Direct Asana API');
        console.log('Google Drive Folder ID:', gdriveFolderId);
        console.log('Formatted Task Title:', taskTitle);
        console.log('Formatted Task Description:', taskDescription);
        console.table(payloadObject);
        console.groupEnd();

        // 3. Submit to Asana or Webhook
        if (asanaPat) {
          await postToAsana(taskTitle, taskDescription);
        } else if (endpointUrl) {
          const epRes = await fetch(endpointUrl, {
            method: 'POST',
            body: formData
          });
          if (!epRes.ok) {
            throw new Error(`Endpoint submission error: ${epRes.statusText}`);
          }
        } else {
          console.log('Application formatted successfully:', { taskTitle, taskDescription });
        }

        if (alertSuccess) alertSuccess.style.display = 'block';
        form.reset();
        if (fileListDisplay) fileListDisplay.textContent = '';
        if (dateInput) dateInput.value = today;
        if (dateDisplay) dateDisplay.textContent = today;

      } catch (err) {
        console.error('Submission error:', err);
        if (alertError) {
          alertError.textContent = err.message || 'There was a problem submitting your application. Please check your entries and try again.';
          alertError.style.display = 'block';
        }
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('is-loading');
        }
      }
    });
  });
}
